import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { CoachAthleteEvalHistoryRow } from '@/services/evaluationApi';

// Sortable columns
type SortColumn = 'name' | 'avg_overall_score' | 'eval_count' | 'last_eval_at';
type SortDirection = 'asc' | 'desc';

interface AthleteRollupTableProps {
  rows: CoachAthleteEvalHistoryRow[];
}

function ScoreCell({ value }: { value: number | null }) {
  if (value === null) {
    return (
      <span style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>—</span>
    );
  }
  const score = Math.round(value);
  const color =
    score >= 80
      ? 'var(--color-accent)'
      : score >= 60
      ? 'var(--color-accent-gold)'
      : 'var(--color-accent-red)';

  return (
    <span
      style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 700,
        fontSize: '14px',
        color,
      }}
    >
      {score}
    </span>
  );
}

export function AthleteRollupTable({ rows }: AthleteRollupTableProps) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection }>({
    column: 'last_eval_at',
    direction: 'desc',
  });

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sort.column) {
        case 'name':
          cmp = (a.display_name || a.email).localeCompare(b.display_name || b.email);
          break;
        case 'avg_overall_score':
          cmp = (a.avg_overall_score ?? -1) - (b.avg_overall_score ?? -1);
          break;
        case 'eval_count':
          cmp = a.eval_count - b.eval_count;
          break;
        case 'last_eval_at':
          cmp = new Date(a.last_eval_at).getTime() - new Date(b.last_eval_at).getTime();
          break;
      }
      return sort.direction === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort]);

  function handleHeaderClick(column: SortColumn) {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'desc' }
    );
  }

  function SortChevron({ column }: { column: SortColumn }) {
    if (sort.column !== column) return null;
    return sort.direction === 'asc' ? (
      <ChevronUpIcon style={{ width: '10px', height: '10px', display: 'inline', marginLeft: '3px' }} />
    ) : (
      <ChevronDownIcon style={{ width: '10px', height: '10px', display: 'inline', marginLeft: '3px' }} />
    );
  }

  if (rows.length === 0) {
    return (
      <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '16px 0' }}>
        No athlete evaluation history yet.
      </p>
    );
  }

  // Column config: label → sort key (null = not sortable)
  const columns: { label: string; sortKey: SortColumn | null }[] = [
    { label: 'Athlete', sortKey: 'name' },
    { label: 'Channels', sortKey: null },
    { label: 'Evals', sortKey: 'eval_count' },
    { label: 'Avg Overall', sortKey: 'avg_overall_score' },
    { label: 'Avg Body Form', sortKey: null },
    { label: 'Avg EOM', sortKey: null },
    { label: 'Last Eval', sortKey: 'last_eval_at' },
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '13px',
        }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
            {columns.map(({ label, sortKey }) => (
              <th
                key={label}
                onClick={sortKey ? () => handleHeaderClick(sortKey) : undefined}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px 8px 0',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  color: sort.column === sortKey ? 'var(--color-accent-blue)' : 'var(--color-text-muted)',
                  whiteSpace: 'nowrap',
                  cursor: sortKey ? 'pointer' : 'default',
                  userSelect: 'none',
                }}
              >
                {label}
                {sortKey && <SortChevron column={sortKey} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={row.user_id}
              style={{
                borderBottom: '1px solid var(--color-border-subtle)',
                cursor: 'pointer',
                transition: 'background-color 120ms ease',
              }}
              onClick={() => navigate(`/athletes/${row.user_id}`)}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                  'rgba(61, 169, 252, 0.04)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
              }}
            >
              {/* Athlete name + email */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    color: 'var(--color-accent-blue)',
                    fontSize: '14px',
                  }}
                >
                  {row.display_name || row.email}
                </p>
                {row.display_name && (
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--color-text-muted)' }}>
                    {row.email}
                  </p>
                )}
              </td>

              {/* Channel chips */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {row.channels.slice(0, 3).map((ch) => (
                    <span
                      key={ch.slug}
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor: 'rgba(61,169,252,0.10)',
                        border: '1px solid rgba(61,169,252,0.20)',
                        color: 'var(--color-accent-blue)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ch.name}
                    </span>
                  ))}
                  {row.channels.length > 3 && (
                    <span
                      style={{
                        fontSize: '11px',
                        color: 'var(--color-text-muted)',
                        alignSelf: 'center',
                      }}
                    >
                      +{row.channels.length - 3}
                    </span>
                  )}
                </div>
              </td>

              {/* Eval count */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                  {row.eval_count}
                </span>
              </td>

              {/* Avg overall */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <ScoreCell value={row.avg_overall_score} />
              </td>

              {/* Avg body form */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <ScoreCell value={row.avg_body_form_score} />
              </td>

              {/* Avg EOM */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <ScoreCell value={row.avg_eom_score} />
              </td>

              {/* Last eval relative time */}
              <td style={{ padding: '12px 0', whiteSpace: 'nowrap' }}>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                  {formatDistanceToNow(new Date(row.last_eval_at), { addSuffix: true })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AthleteRollupTable;
